import { useMemo, type ReactElement, type ReactNode } from 'react';
import { UI_DEFAULTS } from '@gr/core/platform/config/ui-config';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import type { LoadOptions } from '../../../../ui-kit/Combobox/Combobox';
import { FormAlert, FormCombobox, FormTextArea, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { BLOG_FORM_FIELDS, type BlogValues } from '../model/blog-form';
import { timeZoneOption, timeZoneOptions, timeZonePage } from '../model/time-zones';
import styles from './BlogForm.module.scss';

export interface BlogFormProps {
  /** id формы — для кнопки отправки вне её (подвал диалога). */
  id: string;
  initial: BlogValues;
  onSubmit(values: BlogValues): Promise<ServerOutcome>;
  /** Кнопки и сообщения под полями; без них форма отправляется кнопкой с form={id}. */
  footer?(state: { submitting: boolean }): ReactNode;
  /** Сколько поясов автокомплит показывает за раз (ui.timeZones.pageSize). */
  timeZonePageSize?: number;
}

/** WS-UC-1, WS-UC-3: название, описание и часовой пояс блога. */
export function BlogForm({ id, initial, onSubmit, footer, timeZonePageSize = UI_DEFAULTS.timeZones.pageSize }: BlogFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<BlogValues>({ defaultValues: initial, values: initial, fields: BLOG_FORM_FIELDS, onSubmit });
  const { control, formState, watch } = form;
  // WS-RULE-2: пояса движка со сдвигом на сейчас; список в памяти, поиск без паузы.
  const now = useMemo(() => new Date(), []);
  const zones = useMemo(() => timeZoneOptions(Intl.supportedValuesOf('timeZone'), now), [now]);
  const timezone = watch('timezone');
  const loadZones: LoadOptions = async (query, offset) => {
    const page = timeZonePage(zones, query, offset, timeZonePageSize);
    return { items: page.items.map((zone) => ({ value: zone.name, label: zone.label })), hasMore: page.hasMore };
  };
  return (
    <form id={id} className={styles.form} onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextField control={control} name="name" label={t('workspaces.form.name')} rules={{ required: ErrorCode.FieldRequired }} required disabled={formState.isSubmitting} />
      <FormTextArea control={control} name="description" label={t('workspaces.form.description')} rows={3} disabled={formState.isSubmitting} />
      <FormCombobox
        control={control}
        name="timezone"
        label={t('workspaces.form.timezone')}
        hint={t('workspaces.form.timezoneHint')}
        placeholder={t('workspaces.form.timezoneSearch')}
        required
        disabled={formState.isSubmitting}
        load={loadZones}
        searchDelayMs={0}
        selectedLabels={timezone ? { [timezone]: timeZoneOption(timezone, now).label } : undefined}
      />
      {footer?.({ submitting: formState.isSubmitting })}
    </form>
  );
}
